import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Form, message, Input, Space, Button, Modal, Table, Spin } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import CountryChooser from '../../components/CountryChooser/CountryChooser';
import useILTAddresses from '../../gql/hooks/useILTAddresses';
import useAddressCreate from '../../gql/hooks/useAddressCreate';
import useAddressUpdate from '../../gql/hooks/useAddressUpdate';
import useAddressDelete from '../../gql/hooks/useAddressDelete';
import './styles.less';

const ILTAddresses = ( { modifiedCallback, ref } ) => {
    const [ form ] = Form.useForm();
    const { addresses, addressesLoading, addressesError } = useILTAddresses();
    const [ showAddModal, setShowAddModal ] = useState( false );
    const [ showEditModal, setShowEditModal ] = useState( false );
    const [ modalIsSaving, setModalIsSaving ] = useState( false );
    const [ editId, setEditId ] = useState();
    const { createAddress } = useAddressCreate();
    const { saveAddress } = useAddressUpdate();
    const { deleteAddress } = useAddressDelete();
    const [ addressList, setAddressList ] = useState( [] );

    useImperativeHandle( ref, () => ( {
    } ) );

    useEffect( () => {
        if ( addresses ) {
            setAddressList( addresses );
        }
    }, [ addresses ] );

    /*************************************
     *  ANCHOR Add Address
     *************************************/
 
    const handleAddAddressSubmit = async ( values ) => {
        setModalIsSaving( true );

        const response = await createAddress( { ...values } );

        if ( response.success ) { 
            setAddressList( [ 
                {   
                    ...response.response.data.createAddress,
                    key: response.response.data.createAddress.id 
                },
                ...addressList 
            ] );
        } else {
            message.error( 'Error creating address. Please try again later.' );
        }
        
        form.resetFields();
        setModalIsSaving( false );
        setShowAddModal( false );
    };

    const handleAddClick = () => {
        setShowAddModal( true );
    };
    
    const handleAddClose = () => {
        form.submit();
    };
    
    const handleAddCancel = () => {
        setShowAddModal( false );
        form.resetFields();
    };
 
    /*************************************
     *  ANCHOR Edit Address
     *************************************/
    
    const handleEditAddressSubmit = async ( values ) => {
        setModalIsSaving( true );
       
        const input = { 
            id: editId,
            ...values 
        };
 
        const response = await saveAddress( input );

        if ( response.success ) { 
            const updated = addressList.map( i => {
                if ( i.id === editId ) {
                    return {
                        ...response.response.data.patchAddress,
                        key: editId
                    };
                } else {
                    return i;
                }
            } );

            setAddressList( updated );
        } else {
            message.error( 'Error saving address. Please try again later.' );
        }
        
        form.resetFields();
        setModalIsSaving( false );
        setShowEditModal( false );
    };
   
    const handleEditClick = ( id ) => {
        const item = addressList.find( i => i.id === id );
        
        if ( !item ) {
            return;
        }
        
        setEditId( item.id );
        form.setFieldsValue( { label: item.label } );
        form.setFieldsValue( { name: item.name } );
        form.setFieldsValue( { addressLine1: item.addressLine1 } );
        form.setFieldsValue( { addressLine2: item.addressLine2 } );
        form.setFieldsValue( { locality: item.locality } );
        form.setFieldsValue( { administrativeArea: item.administrativeArea } );
        form.setFieldsValue( { postalCode: item.postalCode } );
        form.setFieldsValue( { comment: item.comment } );
        form.setFieldsValue( { countryId: item.country?.id } );

        setShowEditModal( true );
    };

    const handleEditClose = () => {
        form.submit();
    };

    const handleEditCancel = () => {
        setShowEditModal( false );
        form.resetFields();
    };

    /*************************************
     *  ANCHOR Event Handlers
     *************************************/
    
    const handleDeleteClick = async ( i ) => {
        const response = await deleteAddress( i );
        
        if ( response ) {
            setAddressList( addressList.filter( il => il.id !== i ) );
        } else {
            message.error( 'Error deleting address, please try again later.' );
        }
    };

    /*************************************
     *  ANCHOR Table setup
     *************************************/
 
    const tableColumns = [
        {
            dataIndex: 'label',
            key: 'label', 
            title: 'Label',
            sortKey: 'label',
            fixed: 'left',
        },
        {
            dataIndex: 'addressLine1',
            key: 'addressLine1', 
            title: 'Address',
            sortKey: 'addressLine1',
            fixed: 'left',
        },
        {
            dataIndex: 'locality',
            key: 'locality', 
            title: 'City',
            sortKey: 'locality',
            fixed: 'left',
        },
        {
            dataIndex: 'administrativeArea',
            key: 'administrativeArea', 
            title: 'State',
            sortKey: 'administrativeArea',
            fixed: 'left',
        },
        
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => handleEditClick( item.id ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete this address <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            } 
            
        }
    ];

    const getTableHeader = () => {
        return ( 
            <div className="toolbar">
                <div className="left">
                    { /* <h3>Addresses</h3> */ }
                </div>

                <div className="right">
                    <Button   
                        type="text"
                        icon={ <PlusOutlined /> }
                        onClick= { () => handleAddClick() }
                    >
                        Add Address  
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="form-item-addresses">
            
            { addressesLoading && 
                <Spin />
            }
            
            { addressesError && 
                <Alert 
                    type="error"
                    message="Error"
                    description="An unknown error occurred. Please try again later."
                />
            }

            { !addressesLoading && !addressesError &&
                <>
                    <Table
                        columns={ tableColumns }
                        dataSource={ addressList } 
                        title={ () => getTableHeader() }
                        scroll={ { x: 'max-content' } } 
                        pagination={ { 
                            defaultPageSize: 5,
                            showSizeChanger: false 
                        } }
                    />
            
                    { showAddModal && 
                        <Modal
                            title="Add Address"
                            centered
                            open={ showAddModal }
                            onOk={ handleAddClose }
                            onCancel={ handleAddCancel } 
                            confirmLoading={ modalIsSaving }
                            width={ 800 }
                            wrapClassName="address-modal"
                            maskClosable={ false }
                            okText="Save"
                        >
                            <Form
                                form={ form }
                                name="add-form"
                                layout="vertical"
                                onFinish={ handleAddAddressSubmit }
                                scrollToFirstError
                                disabled={ modalIsSaving }
                                initialValues={ 
                                    { 
                                        label: '',
                                        name: '',
                                        addressLine1: '',
                                        addressLine2: '',
                                        locality: '',
                                        administrativeArea: '',
                                        postalCode: '',
                                        comment: '',
                                        countryId: ''
                                    }
                                }
                            >   
                                <Form.Item
                                    name="label"
                                    label="Label"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Label is required',
                                        },
                                        {
                                            max: 50,
                                            validateTrigger: [],
                                            message: 'Label must be less than 50 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="name"
                                    label="Name"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="addressLine1"
                                    label="Address"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Address is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Address must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="locality"
                                    label="City/Locality"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'City/Locality is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'City/Locality must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="administrativeArea"
                                    label="State/Province"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'State/Province is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'State/Province must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="postalCode"
                                    label="Postal Code"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Postal Code is required',
                                        },
                                        {
                                            max: 50,
                                            validateTrigger: [],
                                            message: 'Postal code must be less than 500 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="countryId"
                                    label="Country"
                                    valuePropName="initialValue"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Country is required',
                                        }
                                    ] }
                                >
                                    <CountryChooser />
                                </Form.Item>

                                <Form.Item
                                    name="comment"
                                    label="Comment"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Comment must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>                                
                            </Form>
                        </Modal>
                    }

                    { showEditModal && 
                        <Modal
                            title="Edit Address"
                            centered
                            open={ showEditModal }
                            onOk={ handleEditClose }
                            onCancel={ handleEditCancel } 
                            confirmLoading={ modalIsSaving }
                            width={ 800 }
                            okText="Save"
                            wrapClassName="address-modal"
                        >
                            <Form
                                form={ form }
                                name="edit-form"
                                layout="vertical"
                                onFinish={ handleEditAddressSubmit }
                                scrollToFirstError
                                disabled={ modalIsSaving }
                                initialValues={ 
                                    { 
                                        label: '',
                                        name: '',
                                        addressLine1: '',
                                        addressLine2: '',
                                        locality: '',
                                        administrativeArea: '',
                                        postalCode: '',
                                        comment: '',
                                        countryId: ''
                                    }
                                }
                            >   
                                <Form.Item
                                    name="label"
                                    label="Label"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Label is required',
                                        },
                                        {
                                            max: 50,
                                            validateTrigger: [],
                                            message: 'Label must be less than 50 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="name"
                                    label="Name"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Name must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="addressLine1"
                                    label="Address"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Address is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Address must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="locality"
                                    label="City/Locality"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'City/Locality is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'City/Locality must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="administrativeArea"
                                    label="State/Province"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'State/Province is required',
                                        },
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'State/Province must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    name="postalCode"
                                    label="Postal Code"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Postal Code is required',
                                        },
                                        {
                                            max: 50,
                                            validateTrigger: [],
                                            message: 'Postal code must be less than 500 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>
                                
                                <Form.Item
                                    name="countryId"
                                    label="Country"
                                    valuePropName="initialValue"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Country is required',
                                        }
                                    ] }
                                >
                                    <CountryChooser />
                                </Form.Item>

                                <Form.Item
                                    name="comment"
                                    label="Comment"
                                    rules={ [
                                        {
                                            max: 100,
                                            validateTrigger: [],
                                            message: 'Comment must be less than 100 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>  
                            </Form>
                        </Modal>
                    }
                </>
            }
        </div>
    );
};

export default ILTAddresses;
